/**
 * Platform data for quiz pre-fill, loaded for the standalone /marketing route.
 *
 * Inside the author dashboard these come from AuthorDashboardContext, but
 * /marketing is a top-level route with no such provider — without this the
 * quiz would render with zero platform data and pre-fill nothing at all.
 *
 * These are the author's OWN documents under `authors/{authorId}/…`, already
 * client-readable under existing rules, so they are read directly rather than
 * through a callable. The marketing* collections are the locked-down ones, and
 * nothing here touches those.
 *
 * One-shot reads, not subscriptions: pre-fill is a starting point captured when
 * the quiz opens, and a live listener re-seeding answers mid-quiz would fight
 * the member's own edits.
 */
import { useState, useEffect } from 'react';
import { collection, getDocs, getCountFromServer } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';

export default function usePlatformData() {
  const { authorId } = useAuth();
  const [data, setData] = useState({ books: [], audiobooks: [], subscriberCount: null, loading: true });

  useEffect(() => {
    if (!authorId) {
      setData({ books: [], audiobooks: [], subscriberCount: null, loading: false });
      return undefined;
    }

    let cancelled = false;

    (async () => {
      const safeDocs = async (path) => {
        try {
          const snap = await getDocs(collection(db, ...path));
          return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        } catch (err) {
          console.warn(`[marketing] could not read ${path.join('/')}:`, err?.message || err);
          return [];
        }
      };

      // Count-only aggregate: the subscriber list can be large and the quiz
      // needs nothing but its size. Never pulls addresses into the browser.
      const safeCount = async (path) => {
        try {
          const snap = await getCountFromServer(collection(db, ...path));
          return snap.data().count;
        } catch (err) {
          console.warn(`[marketing] could not count ${path.join('/')}:`, err?.message || err);
          return null;      // null = unknown, so q6 falls back to asking
        }
      };

      const [books, audiobooks, subscriberCount] = await Promise.all([
        safeDocs(['authors', authorId, 'books']),
        safeDocs(['authors', authorId, 'audiobooks']),
        safeCount(['authors', authorId, 'subscribers']),
      ]);

      if (!cancelled) setData({ books, audiobooks, subscriberCount, loading: false });
    })();

    return () => { cancelled = true; };
  }, [authorId]);

  return data;
}
